<template>
    <div class="lead-form-wrapper">
        <lazy-component name="LeadForm" :fetch-data="fetchData" @response="onResponse" @error="handleFetchDataError">
            <template #loading>
                <div class="skeleton skeleton--animation_pulse skeleton--size_md">
                    <div v-for="n in 5" :key="n" class="m-bottom--xs">
                        <div
                            class="skeleton__bone skeleton__bone--image skeleton__bone--rounded skeleton__bone--image skeleton__bone--width_full"
                        ></div>
                    </div>
                    <div
                        class="skeleton__bone skeleton__bone--heading skeleton__bone--rounded skeleton__bone--surtitle skeleton__bone--subheading"
                    ></div>
                    <div
                        class="m-top--m skeleton__bone skeleton__bone--image skeleton__bone--rounded skeleton__bone--image skeleton__bone--width_full"
                    ></div>
                </div>
            </template>
            <template #default="{ component }">
                <component
                    :is="component"
                    ref="leadForm"
                    :is-in-page="formData.isInPage || false"
                    :is-three-steps-dk-promo="formData.isThreeStepsDkPromo"
                    :is-wizard-lead-form="isWizardLeadForm"
                    :is-dk-promo="formData.isDkPromo"
                    :selected-models="selectedModels"
                    v-bind="response"
                    v-on="events"
                    @hook:mounted="onMounted"
                />
            </template>
        </lazy-component>
    </div>
</template>

<script>
import Vue from 'vue';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import { pushGaEvent } from '@v4/common/utils/google-analytics';
import LazyComponent from '@v4/common/components/atoms/LazyComponent';
import { getApiUrl } from '@v4/config';
import { EnumFormIds, ConsentValues, GuIdK } from '@v4/common/components/organisms/LeadFormWrapper/constants';
import { stringifyUrl, getQueryParams } from '@v4/common/utils/url';
import { EventBus } from '@v4/common/utils/event-bus';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import { modalServiceDealerSelection } from '@v4/common/components/mixins/modal-service';

import VueModal from 'vue-js-modal';
// Vue.use automatically prevents you from using the same plugin more than once,
// so trying to install the same plugin several times will install the plugin only once.
// https://v2.vuejs.org/v2/guide/plugins.html#:~:text=someOption%3A%20true%20%7D)-,Vue.,install%20the%20plugin%20only%20once.
Vue.use(VueModal, { dynamic: true, injectModalsContainer: true });

/**
 * LeadForm wrapper component.
 *
 * @version 4.0.0
 * @see [LeadForm style guide](https://leadform-style-guide.drivek.com/)
 * @example ./../examples/LeadFormWrapper.md
 */
export default {
    name: 'LeadFormWrapper',
    components: {
        LazyComponent,
    },
    mixins: [dkModalAware, modalServiceDealerSelection],
    props: {
        /**
         * Form Id.
         * Available values: `submodel`, `version`, `social-lp`, `widget`, `advisor`
         * `editorial`, `multimodel`, `single-offer`, `offer-landing`, `compare`, `dkpromo`
         */
        formId: {
            type: String,
            required: true,
            validator(value) {
                return Object.values(EnumFormIds).includes(value);
            },
        },
        /**
         * Data to send when fetching the form configuration
         */
        formData: {
            type: Object,
            default: null,
        },
        /**
         * Parameters to be use specifically
         * for form/configs/single-offer
         */
        singleOfferParams: {
            type: Object,
            default: () => ({}),
        },
        isForcedProject: {
            type: Boolean,
            default: false,
        },
        isWizardLeadForm: {
            type: Boolean,
            default: false,
        },
        projectId: {
            type: Number,
            default: undefined,
        },
        selectedModels: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            response: {},
            isDirty: false,
        };
    },
    computed: {
        fetchData() {
            return {
                url: stringifyUrl({
                    url: `${getApiUrl()}/dkwp/v2/form/configs/${this.formId}`,
                    query: {
                        ...getQueryParams(),
                        ...this.getMarketingParams(),
                    },
                }),
                method: 'POST',
                data: {
                    ...this.formData,
                    ...this.singleOfferParams,
                    ...(this.isForcedProject && { project_forced: true }),
                    ...(this.projectId && { project_id: this.projectId }),
                },
            };
        },
        events() {
            return {
                ...(this.$listeners || {}),
                submit: (...args) => {
                    if (isFunction(this.$listeners.submit)) {
                        this.$listeners.submit(...args);
                    }

                    this.trackLeadFormSubmit(...args);
                },
                blur: (...args) => {
                    if (isFunction(this.$listeners.blur)) {
                        this.$listeners.blur(...args);
                    }

                    this.trackLeadFormElements(...args);
                },
                focus: (...args) => {
                    if (isFunction(this.$listeners.focus)) {
                        this.$listeners.focus(...args);
                    }

                    this.setLeadFormPrivacyConsentHiddenFields();
                    this.setLeadFormGuidkHiddenField();
                },
                'clicked-note-toggler': (...args) => {
                    if (isFunction(this.$listeners['clicked-note-toggler'])) {
                        this.$listeners['clicked-note-toggler'](...args);
                    }

                    this.trackLeadFormNoteToggler(...args);
                },
                'cta-change': (...args) => {
                    if (isFunction(this.$listeners['cta-change'])) {
                        this.$listeners['cta-change'](...args);
                    }

                    this.trackLeadFormCtaChange(...args);
                },
                'facebook-login': (...args) => {
                    if (isFunction(this.$listeners['facebook-login'])) {
                        this.$listeners['facebook-login'](...args);
                    }

                    this.trackLeadFormFacebookLogin(...args);
                },
                'google-login': (...args) => {
                    if (isFunction(this.$listeners['google-login'])) {
                        this.$listeners['google-login'](...args);
                    }

                    this.trackLeadFormGoogleLogin(...args);
                },
                'after-form-validation': (...args) => {
                    if (isFunction(this.$listeners['after-form-validation'])) {
                        this.$listeners['after-form-validation'](...args);
                    }

                    this.trackLeadFormSubmit(...args);
                },
            };
        },
    },
    methods: {
        onResponse({ response }) {
            this.response = response;
        },
        onMounted() {
            if (window && window.Kameleoon) {
                try {
                    const kameleoonId = window.Kameleoon.API.CurrentVisit.activatedExperiments[0].associatedVariationID;
                    this.setLeadFormHiddenField('dkwp[kameleoonExperimentId]', kameleoonId);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.warn('[Kameleoon] Error getting Kameleoon associated variation id');
                }
            }

            /**
             * Fired when `LeadForm` component has been mounted.
             *
             * @event mounted
             * @property {object} leadFormRef - `LeadForm` component ref
             */
            this.$emit('mounted', { leadFormRef: this.$refs.leadForm });
        },
        /**
         * Sets a hidden field to the LeadForm
         *
         * @param {string} name - property name
         * @param {string} value - property value
         * @public
         */
        setLeadFormHiddenField(name, value) {
            if (!name || !this.$refs.leadForm || !isFunction(this.$refs.leadForm.setProperty)) {
                return;
            }

            this.$refs.leadForm.setProperty(name, value);
        },
        /**
         * Sets whether or not the LeadForm it's in busy status
         *
         * @param {boolean} bool - whether it's busy or not
         * @public
         */
        setLeadFormIsBusy(bool) {
            if (!('isBusy' in this.$refs.leadForm)) {
                return;
            }

            this.$refs.leadForm.isBusy = bool;
        },
        /**
         * Sets LeadForm error message
         *
         * @param {string} message - error message
         * @public
         */
        setLeadFormErrorMessage(message) {
            if (!('error' in this.$refs.leadForm) || typeof this.$refs.leadForm.error !== 'object') {
                return;
            }

            this.$refs.leadForm.error.message = message;
        },
        /**
         * Updates privacy policy text for each CTAs
         *
         * @param {string} privacyPolicyText - new privacy policy text
         * @public
         */
        updateLeadFormCtasPrivacyPolicyText(privacyPolicyText) {
            if (!privacyPolicyText || !Array.isArray(this.response.ctas)) {
                return;
            }

            this.response.ctas = this.response.ctas.map((cta) => ({ ...cta, privacyPolicyText }));
        },
        /**
         * Sets privacy flags
         *
         * @param {array} flagList - flags
         * @public
         */
        setLeadFormPrivacyFlags(flagList = []) {
            if (!this.$refs.leadForm || !this.$refs.leadForm.privacyFlagList || !flagList || !flagList.length) {
                return;
            }

            this.$refs.leadForm.privacyFlagList = flagList;
        },
        /**
         * Sets a value on specific input
         *
         * @param {string} name - name field
         * @param {string} value - value fields
         * @public
         */
        setLeadFormInputField(name, value) {
            if (!name || !this.$refs.leadForm || !isFunction(this.$refs.leadForm.setUserField)) {
                return;
            }

            this.$refs.leadForm.setUserField(name, value);
        },
        /**
         * Sets the privacy consent values as hidden fields
         */
        setLeadFormPrivacyConsentHiddenFields() {
            const { dataLayer = [] } = window;
            if (dataLayer.length === 0) {
                return;
            }

            const fields = dataLayer.filter((item) => ConsentValues.includes(item.event)) || [];
            fields.forEach((field) => {
                this.setLeadFormHiddenField(`dkwp[${field.event}]`, true);
            });
        },
        /**
         * Sets the GUIDK as hidden fields
         */
        setLeadFormGuidkHiddenField() {
            const { dataLayer = [] } = window;
            if (dataLayer.length === 0) {
                return;
            }

            const guidk = dataLayer.find((item) => {
                if (Object.prototype.hasOwnProperty.call(item, GuIdK)) {
                    return item;
                }
            });
            if (guidk) {
                this.setLeadFormHiddenField(`dkwp[${GuIdK}]`, guidk[GuIdK]);
            }
        },
        /**
         * Gets the computed hidden field on specific input
         *
         * @param {string} name - name field
         * @public
         */
        getLeadFormComputedHiddenField(name) {
            if (!name || !this.$refs.leadForm || !this.$refs.leadForm.hiddenFieldsComputed) {
                return;
            }

            return this.$refs.leadForm.hiddenFieldsComputed[name];
        },
        trackLeadFormSubmit(payload) {
            if (!payload || !isObject(payload)) {
                return;
            }

            pushGaEvent(
                'Lead Form',
                `submit ${payload.isValid ? 'valid form' : 'invalid form'}`,
                'CTA button click',
                null,
                { formId: this.formId }
            );
        },
        trackLeadFormElements(payload) {
            if (!payload || !isObject(payload)) {
                return;
            }

            const { item, event } = payload;
            const formId = this.formId;

            if (item && item.name && item.type === 'checkbox') {
                pushGaEvent('Lead Form', `${item.value ? 'completed' : 'skipped'}`, item.name, null, { formId });
            } else if (event && event.target) {
                const name = event.target.id || event.target.name;

                if (name) {
                    pushGaEvent('Lead Form', `${event.target.value ? 'completed' : 'skipped'}`, name, null, { formId });
                }
            }

            if (item && !this.isDirty) {
                EventBus.$emit('LeadForm:input', {
                    formId,
                    formData: this.formData,
                });
                this.isDirty = true;
            }
        },
        trackLeadFormNoteToggler(isOpen) {
            pushGaEvent('Lead Form', `toggled to ${isOpen ? 'open' : 'close'}`, 'additional message', null, {
                formId: this.formId,
            });
        },
        trackLeadFormCtaChange(payload) {
            if (!payload || !isObject(payload)) {
                return;
            }

            const { index, cta } = payload;

            if (!index || !cta || !isObject(cta)) {
                return;
            }

            const position = index + 1;

            pushGaEvent('Lead Form', `CTA ${position} tab click`, 'Lead Form Tab Change', position, {
                ...cta,
                formId: this.formId,
            });
        },
        trackLeadFormFacebookLogin(response = {}) {
            pushGaEvent('Lead Form', 'Facebook button click', `User status: ${response.status || 'unknown'}`, null, {
                formId: this.formId,
            });
        },
        trackLeadFormGoogleLogin() {
            pushGaEvent('Lead Form', 'Click', 'Continue with Google button');
        },
        handleFetchDataError({ error }) {
            if (window.Sentry) {
                const theError = new Error('LeadForm: fetch config API error', {
                    cause: error.event,
                });

                Sentry.captureException(theError, {
                    tags: {
                        category: 'lead-form',
                        formId: this.formId,
                        ...this.formData,
                    },
                });
            }
        },
        getMarketingParams() {
            const marketingParams = JSON.parse(localStorage.getItem('marketingParams') || JSON.stringify({}));
            const { params } = marketingParams;

            return params || {};
        },
    },
};
</script>

<style lang="scss" src="@v4/common/components/organisms/LeadFormWrapper/_index.scss"></style>
